import { supabase } from '@/integrations/supabase/client';
import type { 
  CustomOutletReview, 
  CustomOutletFilters,
  CustomOutletWithDetails,
  OutletOperationResult 
} from '@/types/mediaOutlets';

export const customOutletReviewService = {
  async getPendingReviews(filters?: CustomOutletFilters): Promise<CustomOutletReview[]> {
    try {
      let query = supabase
        .from('custom_outlet_review_queue')
        .select('*')
        .order('submission_count', { ascending: false })
        .order('created_at', { ascending: false });
      
      if (filters?.review_status) {
        query = query.eq('review_status', filters.review_status);
      } else {
        query = query.eq('review_status', 'pending');
      }
      
      if (filters?.min_submissions) {
        query = query.gte('submission_count', filters.min_submissions);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching pending reviews:', error);
      return [];
    }
  },

  async getReviewById(id: string): Promise<CustomOutletWithDetails | null> {
    try {
      const { data, error } = await supabase
        .from('custom_outlet_review_queue')
        .select(`
          *,
          suggested_match:media_outlets!suggested_match_id(*)
        `)
        .eq('id', id)
        .single();
      
      if (error) throw error;
      
      // Transform the join result
      const transformedData: CustomOutletWithDetails = {
        ...data,
        suggested_outlet: Array.isArray(data.suggested_match) ? data.suggested_match[0] : data.suggested_match
      };
      
      return transformedData;
    } catch (error) {
      console.error('Error fetching review by ID:', error);
      return null;
    }
  },

  async submitCustomOutlet(
    customName: string,
    userId: string,
    userType: 'supabase' | 'internal',
    gameId?: string
  ): Promise<OutletOperationResult> {
    try {
      // Check for exact match first
      const { data: existingOutlet } = await supabase
        .from('media_outlets')
        .select('*')
        .ilike('outlet_name', customName.trim())
        .eq('is_active', true)
        .maybeSingle();
      
      if (existingOutlet) {
        return {
          success: false,
          error: 'This outlet already exists in our database. Please select it from the list.',
          data: existingOutlet
        };
      }
      
      // Call database function to handle submission counting
      const { data, error } = await supabase.rpc('increment_custom_outlet_submission', {
        p_custom_outlet_name: customName.trim(),
        p_submitted_by: userId,
        p_submitted_by_type: userType,
        p_game_id: gameId || null
      });
      
      if (error) throw error;
      
      console.log('Custom outlet submitted successfully:', data);
      return { success: true, data };
    } catch (error: any) {
      console.error('Error submitting custom outlet:', error);
      return { success: false, error: error.message };
    }
  },

  async approveCustomOutlet(
    reviewId: string,
    outletData?: {
      outlet_category?: string;
      is_popular?: boolean;
      display_order?: number;
    }
  ): Promise<OutletOperationResult> {
    const adminId = (await supabase.auth.getUser()).data.user?.id;
    if (!adminId) {
      return { success: false, error: 'Not authenticated' };
    }
    try {
      // Get the custom outlet review
      const { data: review, error: reviewError } = await supabase
        .from('custom_outlet_review_queue')
        .select('*')
        .eq('id', reviewId)
        .single();
      
      if (reviewError) throw reviewError;
      
      // Create the new media outlet
      const { data: newOutlet, error: outletError } = await supabase
        .from('media_outlets')
        .insert({
          outlet_name: review.custom_outlet_name,
          outlet_category: outletData.outlet_category,
          is_popular: outletData.is_popular,
          display_order: outletData.display_order,
          is_active: true,
          usage_count: review.submission_count || 0,
          created_by: adminId,
          created_by_type: 'supabase'
        })
        .select()
        .single();
      
      if (outletError) throw outletError;
      
      // Update the review status
      const { error: updateError } = await supabase
        .from('custom_outlet_review_queue')
        .update({
          review_status: 'approved',
          reviewed_by: adminId,
          reviewed_at: new Date().toISOString(),
          reviewed_by_type: 'supabase',
          merged_to_outlet_id: newOutlet.id
        })
        .eq('id', reviewId);
      
      if (updateError) throw updateError;
      
      // Update related audience_mode_sessions
      const { error: sessionError } = await supabase
        .from('audience_mode_sessions')
        .update({ outlet_id: newOutlet.id })
        .eq('custom_queue_id', reviewId);
      
      if (sessionError) {
        console.warn('Error updating audience sessions:', sessionError);
      }
      
      console.log('Custom outlet approved and created:', newOutlet);
      return { success: true, data: newOutlet };
    } catch (error: any) {
      console.error('Error approving custom outlet:', error);
      return { success: false, error: error.message };
    }
  },

  async rejectCustomOutlet(
    reviewId: string,
    reason: string
  ): Promise<OutletOperationResult> {
    const adminId = (await supabase.auth.getUser()).data.user?.id;
    if (!adminId) {
      return { success: false, error: 'Not authenticated' };
    }
    
    try {
      const { data, error } = await supabase
        .from('custom_outlet_review_queue')
        .update({
          review_status: 'rejected',
          reviewed_by: adminId,
          reviewed_at: new Date().toISOString(),
          reviewed_by_type: 'supabase',
          rejection_reason: reason
        })
        .eq('id', reviewId)
        .select()
        .single();
      
      if (error) throw error;
      
      console.log('Custom outlet rejected:', data);
      return { success: true, data };
    } catch (error: any) {
      console.error('Error rejecting custom outlet:', error);
      return { success: false, error: error.message };
    }
  },

  async mergeToExistingOutlet(
    reviewId: string,
    targetOutletId: string
  ): Promise<OutletOperationResult> {
    const adminId = (await supabase.auth.getUser()).data.user?.id;
    if (!adminId) {
      return { success: false, error: 'Not authenticated' };
    }
    
    try {
      // Update the review status
      const { data, error } = await supabase
        .from('custom_outlet_review_queue')
        .update({
          review_status: 'merged',
          reviewed_by: adminId,
          reviewed_at: new Date().toISOString(),
          reviewed_by_type: 'supabase',
          merged_to_outlet_id: targetOutletId
        })
        .eq('id', reviewId)
        .select()
        .single();
      
      if (error) throw error;
      
      // Update related audience_mode_sessions
      const { error: sessionError } = await supabase
        .from('audience_mode_sessions')
        .update({ outlet_id: targetOutletId })
        .eq('custom_queue_id', reviewId);
      
      if (sessionError) {
        console.warn('Error updating audience sessions:', sessionError);
      }
      
      // Increment usage count of target outlet
      const { data: targetOutlet } = await supabase
        .from('media_outlets')
        .select('usage_count')
        .eq('id', targetOutletId)
        .single();
      
      if (targetOutlet) {
        const { error: usageError } = await supabase
          .from('media_outlets')
          .update({ usage_count: (targetOutlet.usage_count || 0) + 1 })
          .eq('id', targetOutletId);
        
        if (usageError) {
          console.warn('Error incrementing target outlet usage:', usageError);
        }
      }
      
      console.log('Custom outlet merged to existing:', data);
      return { success: true, data };
    } catch (error: any) {
      console.error('Error merging custom outlet:', error);
      return { success: false, error: error.message };
    }
  },

  async suggestMatch(reviewId: string, outletId: string): Promise<OutletOperationResult> {
    try {
      const { data, error } = await supabase
        .from('custom_outlet_review_queue')
        .update({ suggested_match_id: outletId })
        .eq('id', reviewId)
        .select()
        .single();
      
      if (error) throw error;
      
      console.log('Match suggested for review:', data);
      return { success: true, data };
    } catch (error: any) {
      console.error('Error suggesting match:', error);
      return { success: false, error: error.message };
    }
  },

  async getAutoSuggestionCandidates(): Promise<CustomOutletReview[]> {
    try {
      const { data, error } = await supabase
        .from('custom_outlet_review_queue')
        .select('*')
        .eq('review_status', 'pending')
        .gte('submission_count', 5)
        .order('submission_count', { ascending: false });
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching auto-suggestion candidates:', error);
      return [];
    }
  },

  async getReviewStats(): Promise<{ pending: number; total: number; approved: number }> {
    try {
      const { count: pending } = await supabase
        .from('custom_outlet_review_queue')
        .select('*', { count: 'exact', head: true })
        .eq('review_status', 'pending');
      
      const { count: total } = await supabase
        .from('custom_outlet_review_queue')
        .select('*', { count: 'exact', head: true });
      
      const { count: approved } = await supabase
        .from('custom_outlet_review_queue')
        .select('*', { count: 'exact', head: true })
        .eq('review_status', 'approved');
      
      return {
        pending: pending || 0,
        total: total || 0,
        approved: approved || 0
      };
    } catch (error) {
      console.error('Error fetching review stats:', error);
      return { pending: 0, total: 0, approved: 0 };
    }
  }
};
